import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw, Globe, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";

export const ProductInfoTab = ({ formData, API }) => {
  const [productInfo, setProductInfo] = useState({
    fetched: null,
    custom: {
      what_for: "",
      how_to_use: "",
      applications: "",
      warnings: "",
      notes: ""
    }
  });
  const [loading, setLoading] = useState(false);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load product info on mount AND when formData.id changes
  useEffect(() => {
    if (formData?.id) {
      loadProductInfo();
    }
  }, [formData?.id]); // Re-load when product ID changes

  const loadProductInfo = async () => {
    if (!formData?.id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/${formData.id}/product-info`);
      
      // Set fetched info
      const fetchedData = response.data.fetched_info || null;
      
      // Set custom info - make sure to handle null/undefined properly
      const customData = response.data.custom_info || {
        what_for: "",
        how_to_use: "",
        applications: "",
        warnings: "",
        notes: ""
      };
      
      setProductInfo({
        fetched: fetchedData,
        custom: customData
      });
      
      console.log("Loaded product info:", { fetchedData, customData }); // Debug log
    } catch (error) {
      console.error("Error loading product info:", error);
      // Initialize with empty data on error
      setProductInfo({
        fetched: null,
        custom: {
          what_for: "",
          how_to_use: "",
          applications: "",
          warnings: "",
          notes: ""
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInfoFromWeb = async () => {
    if (!formData?.id) {
      toast.error("Product must be saved first");
      return;
    }

    setFetchingInfo(true);
    try {
      const response = await axios.post(`${API}/products/${formData.id}/fetch-info`, {
        product_id: formData.id,
        product_name: formData.name,
        category: formData.category,
        description: formData.description
      });

      if (response.data.success) {
        setProductInfo(prev => ({
          ...prev,
          fetched: response.data.fetched_info
        }));
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to fetch product information");
      console.error("Error fetching info:", error);
    } finally {
      setFetchingInfo(false);
    }
  };

  const saveCustomInfo = async () => {
    if (!formData?.id) {
      toast.error("Product must be saved first");
      return;
    }

    setSaving(true);
    try {
      const response = await saveWithRetry(() => axios.put(`${API}/products/${formData.id}/product-info`, productInfo.custom));
      
      if (response.data.success) {
        toast.success("Product information saved successfully");
        // Reload the data to confirm it was saved
        await loadProductInfo();
      }
    } catch (error) {
      toast.error(errorMessage(error, "Failed to save product information"));
      console.error("Error saving info:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCustomChange = (field, value) => {
    setProductInfo(prev => ({
      ...prev,
      custom: {
        ...prev.custom,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading product information...</p>
        </div>
      </div>
    );
  }

  if (!formData?.id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500">Please save the product first before adding product information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Module 1: Fetched Information from Web */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Product Information from Web
              </CardTitle>
              <CardDescription>
                Automatically fetch product details, usage, and applications from the internet
              </CardDescription>
            </div>
            <Button
              onClick={fetchInfoFromWeb}
              disabled={fetchingInfo || !formData?.id}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {fetchingInfo ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Fetch Info from Web
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {productInfo.fetched ? (
            <>
              {productInfo.fetched.description && (
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Description</Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-700">
                    {productInfo.fetched.description}
                  </div>
                </div>
              )}
              
              {productInfo.fetched.usage && (
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Usage Information</Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-700">
                    {productInfo.fetched.usage}
                  </div>
                </div>
              )}
              
              {productInfo.fetched.applications && (
                <div>
                  <Label className="text-sm font-semibold text-gray-700">Applications</Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-700">
                    {productInfo.fetched.applications}
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between text-xs text-gray-500">
                {productInfo.fetched.fetched_at && (
                  <span>Last fetched: {new Date(productInfo.fetched.fetched_at).toLocaleString()}</span>
                )}
                {productInfo.fetched.sources && productInfo.fetched.sources.length > 0 && (
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                    Sources: {productInfo.fetched.sources.join(", ")}
                  </span>
                )}
              </div>
              
              {productInfo.fetched.note && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  💡 {productInfo.fetched.note}
                </div>
              )}
              
              {!productInfo.fetched.description && !productInfo.fetched.usage && (
                <div className="text-sm text-gray-500 italic">
                  No information found from web sources. Try adding custom information below, or contact support to configure Google Custom Search API for better results.
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No information fetched yet</p>
              <p className="text-sm mt-1">Click "Fetch Info from Web" to get product details</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Module 2: Custom Information Entry */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Custom Product Information
              </CardTitle>
              <CardDescription>
                Add your own product details, usage instructions, and notes
              </CardDescription>
            </div>
            <Button
              onClick={saveCustomInfo}
              disabled={saving || !formData?.id}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Custom Info"
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>What is this product used for?</Label>
            <Textarea
              value={productInfo.custom.what_for || ""}
              onChange={(e) => handleCustomChange("what_for", e.target.value)}
              placeholder="Describe what this product is used for..."
              className="mt-1 min-h-[100px]"
            />
          </div>

          <div>
            <Label>How to use / Directions</Label>
            <Textarea
              value={productInfo.custom.how_to_use || ""}
              onChange={(e) => handleCustomChange("how_to_use", e.target.value)}
              placeholder="Enter directions on how to use this product..."
              className="mt-1 min-h-[100px]"
            />
          </div>

          <div>
            <Label>Applications</Label>
            <Textarea
              value={productInfo.custom.applications || ""}
              onChange={(e) => handleCustomChange("applications", e.target.value)}
              placeholder="List the applications where this product can be used..."
              className="mt-1 min-h-[80px]"
            />
          </div>

          <div>
            <Label>Warnings / Precautions</Label>
            <Textarea
              value={productInfo.custom.warnings || ""}
              onChange={(e) => handleCustomChange("warnings", e.target.value)}
              placeholder="Any warnings or precautions for this product..."
              className="mt-1 min-h-[80px]"
            />
          </div>

          <div>
            <Label>Additional Notes</Label>
            <Textarea
              value={productInfo.custom.notes || ""}
              onChange={(e) => handleCustomChange("notes", e.target.value)}
              placeholder="Any other notes or information about this product..."
              className="mt-1 min-h-[80px]"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
